import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { StatusBadge } from "./StatusBadge";
import { resolveImg } from "../lib/api";
import { toast } from "sonner";

const productState = (p) => {
  const comingSoon = p.status === "coming_soon";
  return { comingSoon, buyable: !comingSoon && p.stock > 0, soldOut: p.status === "sold_out" || (!comingSoon && p.stock <= 0) };
};

const SoldOutOverlay = ({ slug, lang }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-[#0B0A08]/35" data-testid={`sold-out-overlay-${slug}`}>
    <div className="text-center">
      <p className="serif italic text-[#F2EBE1] text-3xl md:text-4xl tracking-wide drop-shadow-lg">Sold Out</p>
      <p className="text-[10px] uppercase tracking-[0.4em] text-[#C49A6C] mt-2">{lang === "ar" ? "نفدت الكمية" : "Fully Reserved"}</p>
    </div>
  </div>
);

const CardMedia = ({ product, name, state, lang, t, onQuickView }) => (
  <Link to={`/product/${product.slug}`} className="block relative overflow-hidden bg-[#141311]">
    <div className="absolute top-4 start-4 z-10 flex flex-col gap-2">
      {product.status === "limited_release" && !state.soldOut && <StatusBadge status="limited_release" />}
      {state.comingSoon && <StatusBadge status="coming_soon" />}
    </div>
    <div className="aspect-[3/4] overflow-hidden relative">
      {product.images?.[0] ? (
        <img src={resolveImg(product.images[0])} alt={name}
          className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${state.soldOut ? "grayscale brightness-75" : ""}`} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#6E685F] serif text-4xl">OMR</div>
      )}
      {state.soldOut && <SoldOutOverlay slug={product.slug} lang={lang} />}
    </div>
    {onQuickView && (
      <button onClick={(e) => { e.preventDefault(); onQuickView(product); }} data-testid={`quick-view-${product.slug}`}
        className="absolute bottom-4 inset-x-4 bg-[#0B0A08]/85 backdrop-blur text-[#F2EBE1] text-[11px] uppercase tracking-[0.2em] py-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
        {t("quick_view")}
      </button>
    )}
  </Link>
);

export const ProductCard = ({ product, onQuickView }) => {
  const { t, loc, lang, money } = useLang();
  const { addItem } = useCart();
  const state = productState(product);
  const name = loc(product, "name");

  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!state.buyable) return;
    addItem(product, 1);
    toast.success(t("added_to_bag"));
  };

  return (
    <div className="group" data-testid={`product-card-${product.slug}`}>
      <CardMedia product={product} name={name} state={state} lang={lang} t={t} onQuickView={onQuickView} />
      <div className="pt-5">
        <div className="flex items-baseline justify-between gap-4">
          <Link to={`/product/${product.slug}`}><h3 className="serif text-xl md:text-2xl text-[#F2EBE1] leading-tight">{name}</h3></Link>
          <span className="text-sm text-[#C49A6C] whitespace-nowrap">{money(product.price)}</span>
        </div>
        <p className="text-xs uppercase tracking-[0.15em] text-[#8A857D] mt-2">{product.size} · {product.concentration}</p>
        <Link to={`/product/${product.slug}`} data-testid={`card-details-${product.slug}`} className="inline-block mt-3 text-[11px] uppercase tracking-[0.2em] text-[#C49A6C] link-underline">
          {t("view_details")} →
        </Link>
        <div className="mt-3 h-9">
          {state.buyable ? (
            <button onClick={add} data-testid={`card-add-${product.slug}`} className="text-[11px] uppercase tracking-[0.2em] text-[#F2EBE1] link-underline">{t("add_to_bag")} →</button>
          ) : (
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#6E685F]">{state.comingSoon ? t("coming_soon") : t("sold_out")}</span>
          )}
        </div>
      </div>
    </div>
  );
};
